import React, { useEffect, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { TFunction } from '@lyearn/i18n';
import { ChevronDownFill, ChevronUpFill, LyLogoWhite } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Stack, Typography } from '@lyearn/ui';
import { linkStyles } from '@/components/MessageItem/MessageBody/styles';
import { getAdaptedMarkup } from '@/components/MessageItem/MessageBody/utils';

import { ChatbotMessageType } from '../../types';
import {
  anchorNone,
  mentionHelperStyles,
  reasoningBulletStyles,
  reasoningStyles,
  shimmerEffect,
} from './styles';

// Derives the single status line shown next to the toggle from the reasoning
// text: the most recent step heading. The reasoning post is stored as HTML
// (`<div class="unit"><h2>…</h2>…</div>`, with a trailing "Done" unit once the
// turn finishes), so we pull the last heading element's text — which keeps the
// header on the latest real step (e.g. "Found 3 relevant sources") rather than
// the "Done" marker. A markdown branch is kept for safety/older content.
const getCurrentStatus = (textContent: string, fallback: string): string => {
  const raw = (textContent || '').replace(/\[\[[^\]]+\]\]/g, '').trim();
  if (!raw) {
    return fallback;
  }

  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (looksLikeHtml && typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = raw;
    const headings = Array.from(container.querySelectorAll('h1,h2,h3,h4,h5,h6'));
    const lastHeading = headings[headings.length - 1]?.textContent?.trim();
    if (lastHeading) {
      return lastHeading;
    }
    const text = (container.textContent || '').trim();
    if (text && text.toLowerCase() !== 'done') {
      return text;
    }
    return fallback;
  }

  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const lastHeading = [...lines].reverse().find((line) => /^#{1,6}\s/.test(line));
  if (lastHeading) {
    return lastHeading.replace(/^#{1,6}\s*/, '');
  }
  return lines[lines.length - 1] || fallback;
};

const injectBullets = (textContent: string) => {
  return textContent
    .replaceAll(
      '[[DONE_ICON]]',
      `<div class="reasoning-bullet-container reasoning-bullet-container-done"><svg width="16" height="16" viewBox="0 0 24 24" class=" block fill-current"><path fill-rule="evenodd" clip-rule="evenodd" d="M14.705 8.395l-3.784 4.969-1.633-2.087a.999.999 0 10-1.575 1.232l2.43 3.107a1 1 0 00.789.383h.007a.994.994 0 00.788-.394l4.569-6a.999.999 0 10-1.591-1.21zM12 20c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm0-18C6.477 2 2 6.478 2 12s4.477 10 10 10 10-4.478 10-10S17.523 2 12 2z"></path></svg></div>`,
    )
    .replaceAll(
      '[[REASONING_BULLET]]',
      `<div class="reasoning-bullet-container"><div class="reasoning-bullet"></div></div>`,
    );
};

const ReasoningMessage = ({
  textContent,
  id,
  t,
  isLastMessage,
  scrollDown,
}: ChatbotMessageType & { t: TFunction; isLastMessage: boolean; scrollDown: () => void }) => {
  const { isMobile } = useScreen();
  // Collapsed by default; users open the dropdown to see the full step history.
  const [expanded, { toggle }] = useToggle(false);

  const currentStatus = useMemo(
    () => getCurrentStatus(textContent, t('Common:Thinking')),
    [textContent, t],
  );

  // Gently follow the changing status while the latest turn is still streaming.
  // `scrollDown` is gated on the user being at the bottom, so scrolling up sticks.
  useEffect(() => {
    if (isLastMessage) {
      scrollDown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus, isLastMessage]);

  return (
    <Stack>
      <Stack className="flex-shrink-0 mt-10" justifyContent="start">
        <LyLogoWhite height={30} width={30} />
      </Stack>

      <Stack
        className={cn(
          'rounded-8 self-start justify-self-start w-full',
          isMobile ? 'py-12 mr-12 ml-6' : 'p-12 mr-[3.6rem] ml-12 ',
        )}
        direction="col"
        gap={16}>
        <Stack>
          <LinkButton endIcon={expanded ? <ChevronUpFill /> : <ChevronDownFill />} onClick={toggle}>
            <Typography
              className={isLastMessage ? shimmerEffect : ''}
              color="text-text-secondary"
              variant="body-short-02">
              {currentStatus}
            </Typography>
          </LinkButton>
        </Stack>

        <Stack
          aria-labelledby={id}
          className={cn(
            anchorNone,
            linkStyles,
            expanded ? 'opacity-100 max-h-[1000px]' : 'opacity-0 max-h-[0px] -mb-20',
            'transition-all duration-200 ease-in-out overflow-hidden px-[8px]',
          )}
          direction="col">
          <Typography
            as="div"
            className={cn(
              'markup-container',
              reasoningStyles,
              reasoningBulletStyles,
              mentionHelperStyles,
            )}
            color="text-text-secondary"
            variant="body-short-02">
            <span
              dangerouslySetInnerHTML={getAdaptedMarkup(t, injectBullets(textContent), null)}
              className={cn('markup-container')}
            />
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default React.memo(ReasoningMessage);
